import { Component, computed, Inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import { CRMApiService, CrmObject } from '@vendasta/crm';
import { IAMService, User, UserIdentifier } from '@vendasta/iamv2';
import { MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/meeting-analysis/static';
import { map } from 'rxjs/operators';
import { RouterModule } from '@angular/router';
import { ParticipantComponent } from '../participants/participant/participant.component';
import { AttendeeComponent } from '../attendees/attendee/attendee.component';
import { MatMenuModule } from '@angular/material/menu';
import { AvatarGroupComponent } from '@vendasta/galaxy/avatar';
import { getFormattedContactDisplayName, getFormattedDisplayName } from '../../users.utility';
import { CrmFieldService } from '@galaxy/crm/services/field-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface DisplayItem {
  type: 'contact' | 'user';
  contact?: CrmObject;
  user?: User;
  id: string;
}

@Component({
  selector: 'meeting-analysis-guests',
  standalone: true,
  imports: [
    RouterModule,
    ParticipantComponent,
    AttendeeComponent,
    MatMenuModule,
    AvatarGroupComponent,
    TranslateModule,
  ],
  template: `
    <div class="combined">
      @if (useAvatarGroup()) {
        <glxy-avatar-group class="avatar-group" [avatarList]="avatarList()" [avatarWidth]="28"></glxy-avatar-group>
      } @else {
        @for (item of displayItems(); track item.id) {
          <div class="attendee-item">
            @if (item.type === 'contact' && item.contact) {
              <meeting-analysis-participant
                [contact]="item.contact"
                [linkToContactPage]="false"
                [shape]="'square'"
              ></meeting-analysis-participant>
            } @else if (item.type === 'contact' && !item.contact) {
              <meeting-analysis-attendee
                [user]="null"
                [shape]="'square'"
                [showProfilePicture]="false"
                defaultInformationKey="MEETING_DETAILS.CONTACT_NOT_AVAILABLE"
              ></meeting-analysis-attendee>
            } @else {
              <meeting-analysis-attendee
                [user]="item.user"
                [shape]="'square'"
                [showProfilePicture]="false"
                defaultInformationKey="MEETING_DETAILS.TEAM_MEMBER_NOT_AVAILABLE"
              ></meeting-analysis-attendee>
            }
          </div>
        }
        @if (morePeopleText()) {
          <div class="count" (click)="morePeopleClicked()">{{ morePeopleText() }}</div>
        }
      }
    </div>
  `,
  styleUrls: ['./guests.component.scss'],
})
export class GuestsComponent {
  useAvatarGroup = input<boolean>(false);
  contactIds = input<string[]>([]);
  userIds = input<string[]>([]);
  readonly jumpToGuestsTab = output<boolean>();

  // Computed properties to ensure unique IDs
  uniqueContactIds = computed(() => {
    const ids = this.contactIds();
    return [...new Set(ids)];
  });

  uniqueUserIds = computed(() => {
    const ids = this.userIds();
    return [...new Set(ids)];
  });

  contacts$ = combineLatest([this.namespace$, toObservable(this.uniqueContactIds)]).pipe(
    switchMap(([namespace, contactIds]) => {
      if (contactIds.length === 0) {
        return of({ contactIds: [] as string[], contacts: [] as (CrmObject | null)[] });
      }
      return this.contactService
        .getMultiContact({
          namespace: namespace,
          crmObjectIds: contactIds,
        })
        .pipe(map((response) => ({ contactIds, contacts: response?.crmObjects || [] })));
    }),
    map(({ contactIds, contacts }) => {
      const contactMap = new Map(contacts.filter((c): c is CrmObject => c !== null).map((c) => [c.crmObjectId, c]));
      return contactIds.map((id) => ({ id, contact: contactMap.get(id) || null }));
    }),
  );
  contactItems = toSignal(
    this.contacts$.pipe(
      map((items) =>
        items.map(
          (item) =>
            ({
              type: 'contact',
              contact: item.contact,
              id: item.id,
            }) as DisplayItem,
        ),
      ),
    ),
  );

  users$ = toObservable(this.uniqueUserIds).pipe(
    switchMap((userIds) => {
      if (userIds.length === 0) {
        return of({ userIds: [] as string[], users: [] as (User | null)[] });
      }
      const userIdentifiers = userIds.map((userId) => ({ userId }) as UserIdentifier);
      return this.iamService.getMultiUsers(userIdentifiers).pipe(map((users) => ({ userIds, users })));
    }),
    map(({ userIds, users }) => {
      const userMap = new Map(users.filter((u): u is User => u !== null).map((u) => [u.userId, u]));
      return userIds.map((id) => ({ id, user: userMap.get(id) || null }));
    }),
  );
  userItems = toSignal(
    this.users$.pipe(
      map((items) =>
        items.map(
          (item) =>
            ({
              type: 'user',
              user: item.user,
              id: item.id,
            }) as DisplayItem,
        ),
      ),
    ),
  );

  displayItems = computed(() => {
    const allItems = [...(this.contactItems() || []), ...(this.userItems() || [])];
    const sorted = allItems.sort((a, b) => {
      const aHasData = a.type === 'contact' ? !!a.contact : !!a.user;
      const bHasData = b.type === 'contact' ? !!b.contact : !!b.user;
      return (aHasData ? 0 : 1) - (bHasData ? 0 : 1);
    });
    return sorted.slice(0, 3);
  });

  avatarList = computed(() => {
    const allItems = [...(this.contactItems() || []), ...(this.userItems() || [])];
    const sorted = allItems.sort((a, b) => {
      const aHasData = a.type === 'contact' ? !!a.contact : !!a.user;
      const bHasData = b.type === 'contact' ? !!b.contact : !!b.user;
      return (aHasData ? 0 : 1) - (bHasData ? 0 : 1);
    });
    return sorted.map((item) => {
      if (item.type === 'contact') {
        return {
          name: item.contact
            ? getFormattedContactDisplayName(item.contact, this.fieldsService)
            : this.translateService.instant('MEETING_DETAILS.INFO_NOT_AVAILABLE'),
          src: '',
        };
      } else {
        return {
          name: item.user
            ? getFormattedDisplayName(item.user)
            : this.translateService.instant('MEETING_DETAILS.INFO_NOT_AVAILABLE'),
          src: item.user?.profileImageUrl || '',
        };
      }
    });
  });

  morePeopleText = computed(() => {
    const total = (this.contactItems()?.length || 0) + (this.userItems()?.length || 0);
    const moreCount = total - 3;
    if (moreCount > 0) {
      return `+${moreCount}`;
    }
    return '';
  });

  constructor(
    private readonly contactService: CRMApiService,
    private readonly iamService: IAMService,
    private readonly fieldsService: CrmFieldService,
    private readonly translateService: TranslateService,
    @Inject(MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$) private readonly namespace$: Observable<string>,
  ) {}

  morePeopleClicked(): void {
    this.jumpToGuestsTab.emit(true);
  }
}
